/**
 * FloatingChat — a draggable, persistent chat window that hovers over the app.
 * Rendered at App level so it survives route changes.
 */
import { useState, useRef, useEffect, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { X, Minimize2, Send, GripVertical, ExternalLink, ArrowLeft } from "lucide-react";
import { useChatContext } from "@/contexts/chat-context";
import { useLocation } from "wouter";
import { format, isToday, isYesterday } from "date-fns";

interface Message {
  id: string;
  sender_id: string;
  content: string;
  created_at: string;
  sender_name: string;
  context_type?: string;
  context_title?: string;
  context_url?: string;
}

function dayLabel(dateStr: string) {
  const d = new Date(dateStr);
  if (isToday(d)) return "Today";
  if (isYesterday(d)) return "Yesterday";
  return format(d, "MMM d, yyyy");
}

function groupByDay(messages: Message[]) {
  const groups: { label: string; messages: Message[] }[] = [];
  for (const msg of messages) {
    const label = dayLabel(msg.created_at);
    const last = groups[groups.length - 1];
    if (last && last.label === label) {
      last.messages.push(msg);
    } else {
      groups.push({ label, messages: [msg] });
    }
  }
  return groups;
}

export function FloatingChat() {
  const { floatingChat, closeFloat, setFloatPosition } = useChatContext();
  const [, navigate] = useLocation();
  const [input, setInput] = useState("");
  const [minimized, setMinimized] = useState(false);
  const dragRef = useRef<{ startX: number; startY: number; startPosX: number; startPosY: number } | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const qc = useQueryClient();

  const { userId, userName, isOpen, position } = floatingChat;

  const { data, refetch } = useQuery({
    queryKey: ["/api/communicate/thread", userId],
    queryFn: async () => {
      if (!userId) return { messages: [] };
      const res = await fetch(`/api/communicate/thread/${userId}`);
      if (!res.ok) throw new Error("Failed to fetch");
      return res.json() as Promise<{ conversationId: string | null; messages: Message[] }>;
    },
    enabled: !!userId && isOpen,
    refetchInterval: 5000,
  });

  // Scroll to bottom on new messages
  useEffect(() => {
    if (!minimized) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [data?.messages.length, minimized]);

  // Polling refetch when open
  useEffect(() => {
    if (!isOpen || !userId) return;
    const id = setInterval(refetch, 5000);
    return () => clearInterval(id);
  }, [isOpen, userId, refetch]);

  const sendMutation = useMutation({
    mutationFn: async (content: string) => {
      const res = await fetch(`/api/communicate/thread/${userId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ content }),
      });
      if (!res.ok) throw new Error("Failed to send");
      return res.json();
    },
    onSuccess: () => {
      setInput("");
      refetch();
      qc.invalidateQueries({ queryKey: ["/api/communicate/unread-count"] });
      qc.invalidateQueries({ queryKey: ["/api/communicate/threads"] });
    },
  });

  const handleSend = () => {
    const text = input.trim();
    if (!text || !userId || sendMutation.isPending) return;
    sendMutation.mutate(text);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Drag handling
  const onHeaderMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if ((e.target as HTMLElement).closest("button")) return;
      dragRef.current = { startX: e.clientX, startY: e.clientY, startPosX: position.x, startPosY: position.y };
      e.preventDefault();
    },
    [position]
  );

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragRef.current) return;
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      setFloatPosition({
        x: Math.max(0, Math.min(window.innerWidth - 380, dragRef.current.startPosX + dx)),
        y: Math.max(0, Math.min(window.innerHeight - 60, dragRef.current.startPosY + dy)),
      });
    };
    const onUp = () => { dragRef.current = null; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [setFloatPosition]);

  if (!isOpen || !userId) return null;

  const messages = data?.messages ?? [];
  const groups = groupByDay(messages);
  const initials = (userName ?? "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

  return (
    <div
      className="fixed z-[9999] flex flex-col bg-background border border-border rounded-xl shadow-2xl overflow-hidden select-none"
      style={{
        left: position.x,
        top: position.y,
        width: 380,
        height: minimized ? "auto" : 520,
        minWidth: 320,
      }}
    >
      {/* Header / drag handle */}
      <div
        className="flex items-center gap-2.5 px-3 py-2.5 bg-primary text-primary-foreground cursor-grab active:cursor-grabbing shrink-0"
        onMouseDown={onHeaderMouseDown}
      >
        <GripVertical className="w-3.5 h-3.5 opacity-50" />
        <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold shrink-0">
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold truncate leading-none">{userName}</p>
          <p className="text-[10px] opacity-70 mt-0.5">Chat</p>
        </div>
        <div className="flex gap-1 items-center">
          <Button
            size="sm"
            variant="ghost"
            className="h-6 w-6 p-0 text-primary-foreground hover:bg-white/20"
            onClick={() => navigate(`/communicate?thread=${userId}`)}
            title="Open in Communicate"
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-6 w-6 p-0 text-primary-foreground hover:bg-white/20"
            onClick={() => setMinimized((v) => !v)}
            title={minimized ? "Expand" : "Minimize"}
          >
            <Minimize2 className="w-3.5 h-3.5" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-6 w-6 p-0 text-primary-foreground hover:bg-white/20"
            onClick={closeFloat}
            title="Close"
          >
            <X className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {!minimized && (
        <>
          {/* Messages */}
          <ScrollArea className="flex-1 px-3 py-2">
            {messages.length === 0 ? (
              <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">
                No messages yet. Say hi!
              </div>
            ) : (
              <div className="space-y-4">
                {groups.map((group) => (
                  <div key={group.label}>
                    <div className="flex items-center gap-2 my-2">
                      <div className="flex-1 h-px bg-border" />
                      <span className="text-[10px] text-muted-foreground">{group.label}</span>
                      <div className="flex-1 h-px bg-border" />
                    </div>
                    <div className="space-y-2">
                      {group.messages.map((msg) => {
                        const isMine = msg.sender_id !== userId;
                        return (
                          <div key={msg.id} className={`flex ${isMine ? "justify-end" : "justify-start"}`}>
                            <div className="max-w-[82%] space-y-1">
                              {!isMine && (
                                <p className="text-[10px] text-muted-foreground ml-1">{msg.sender_name}</p>
                              )}
                              <div className={`rounded-2xl px-3 py-2 text-sm leading-relaxed ${
                                isMine ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"
                              }`}>
                                {msg.content}
                              </div>
                              {msg.context_title && msg.context_url && (
                                <a
                                  href={msg.context_url}
                                  className="flex items-center gap-1.5 text-[10px] text-primary hover:underline ml-1"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    navigate(msg.context_url!);
                                  }}
                                >
                                  <ExternalLink className="w-2.5 h-2.5" />
                                  {msg.context_title}
                                </a>
                              )}
                              <p className={`text-[10px] text-muted-foreground ${isMine ? "text-right" : "ml-1"}`}>
                                {format(new Date(msg.created_at), "h:mm a")}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>
            )}
          </ScrollArea>

          {/* Input */}
          <div className="px-3 py-2.5 border-t bg-background shrink-0">
            <div className="flex gap-2 items-end">
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type a message…"
                className="min-h-[40px] max-h-24 resize-none text-sm"
                rows={1}
                disabled={sendMutation.isPending}
              />
              <Button
                size="sm"
                className="shrink-0 h-9 w-9 p-0"
                disabled={!input.trim() || sendMutation.isPending}
                onClick={handleSend}
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/** Banner shown on pages navigated to from a chat context link */
export function ChatReturnBanner({
  userId,
  userName,
  onDismiss,
}: {
  userId: string;
  userName: string;
  onDismiss?: () => void;
}) {
  const [, navigate] = useLocation();
  const { openFloat } = useChatContext();

  return (
    <div className="fixed top-0 left-0 right-0 z-[9998] bg-primary text-primary-foreground px-4 py-2 flex items-center gap-3 shadow-md">
      <Button
        size="sm"
        variant="ghost"
        className="text-primary-foreground hover:bg-white/20 gap-1.5"
        onClick={() => {
          openFloat(userId, userName);
          navigate(`/communicate?thread=${userId}`);
          onDismiss?.();
        }}
      >
        <ArrowLeft className="w-4 h-4" />
        Back to conversation with {userName}
      </Button>
      <div className="flex-1" />
      <Button
        size="sm"
        variant="ghost"
        className="text-primary-foreground hover:bg-white/20 h-7 w-7 p-0"
        onClick={onDismiss}
        aria-label="Dismiss banner"
      >
        <X className="w-4 h-4" />
      </Button>
    </div>
  );
}
